import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import {
  ColumnFiltersState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';

import { FilterPopover } from './filter-popover';
import { Doc } from '@/api/types/backend-dto-types';
import { Chip } from '@/new/components/chip';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '@/new/components/shadcn/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { Toggle } from '@/new/components/shadcn/toggle';
import { SortIcon } from '@/new/components/sort-icon';
import { TableFooter } from '@/new/components/table-footer';
import { TableNullField } from '@/new/components/table-null-field';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import {
  arrInArr,
  includedInArr,
  selectedRowsFilter,
  sortArrayLength,
  sortStringWithEmptyLast,
} from '@/new/lib/table-utils';
import { cn } from '@/new/lib/utils';
import { buildClientSideFilterPills } from '@/utils/buildFilterPills';

const fallbackTableData: Doc[] = [];
const columnHelper = createColumnHelper<Doc>();

const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <div className="flex w-full items-center justify-center text-center">
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
        />
      </div>
    ),
    cell: (info) => (
      <div className="flex w-full min-w-5 items-center justify-center text-center">
        <Checkbox
          checked={info.row.getIsSelected()}
          disabled={!info.row.getCanSelect()}
          onCheckedChange={info.row.getToggleSelectedHandler()}
        />
      </div>
    ),
  }),
  columnHelper.accessor('file.name', {
    id: 'file.name',
    header: () => <span>Filename</span>,
    cell: (info) => <div className="line-clamp-1 max-w-96 min-w-32 break-all">{info.getValue()}</div>,
    sortingFn: sortStringWithEmptyLast,
    meta: {
      sortIcon: 'string',
    },
  }),
  columnHelper.accessor('author', {
    header: () => <span>Author</span>,
    cell: (info) =>
      info.getValue() ? <div className="line-clamp-1 max-w-64 break-all">{info.getValue()}</div> : <TableNullField />,
    sortingFn: sortStringWithEmptyLast,
    meta: {
      sortIcon: 'string',
    },
  }),
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) =>
      info.getValue() ? (
        <Chip className={cn(CHIP_COLOURS.type)}>
          <div className="line-clamp-1 max-w-28 text-ellipsis">{info.getValue()}</div>
        </Chip>
      ) : (
        <TableNullField />
      ),
    filterFn: includedInArr,
    sortingFn: sortStringWithEmptyLast,
    meta: {
      sortIcon: 'string',
    },
  }),
  columnHelper.accessor('file.extension', {
    id: 'file.extension',
    header: () => <span>Extension</span>,
    cell: (info) =>
      info.getValue() ? (
        <Chip className={cn(CHIP_COLOURS.extension)}>
          <div className="line-clamp-1 max-w-28 text-ellipsis">{info.getValue().toUpperCase()}</div>
        </Chip>
      ) : (
        <TableNullField />
      ),
    filterFn: includedInArr,
    meta: {
      sortIcon: 'string',
    },
  }),
  columnHelper.accessor('issues', {
    header: () => <span>Issues</span>,
    cell: (info) => {
      const issues = info.getValue();

      if (!issues.length) return <TableNullField />;

      return (
        <Popover>
          <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button size="sm" className="gap-0 py-0.5">
              <span>
                {issues.length} <span className="font-normal">{issues.length === 1 ? 'issue' : 'issues'}</span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-min" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap gap-2.5">
              {issues.map((issue) => (
                <Chip key={issue} className={cn(CHIP_COLOURS.issue)}>
                  {issue}
                </Chip>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    },
    filterFn: arrInArr,
    sortingFn: sortArrayLength,
    meta: {
      sortIcon: 'number',
    },
  }),
  columnHelper.accessor('factsCount', {
    header: () => <span>No. of Facts</span>,
    meta: {
      sortIcon: 'number',
    },
  }),
  columnHelper.accessor('date', {
    header: () => <span>Document Date</span>,
    cell: (info) => (
      <span className="whitespace-nowrap">
        {new Date(info.getValue() as unknown as string).toLocaleString('en-GB', {
          dateStyle: 'medium',
        })}
      </span>
    ),
  }),
];

interface DocPickerTableProps {
  docs: Doc[] | undefined;
  rowSelection: RowSelectionState;
  onRowSelectionChange: Dispatch<SetStateAction<RowSelectionState>>;
}

export function DocPickerTable({ docs, rowSelection, onRowSelectionChange }: DocPickerTableProps) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [showSelected, setShowSelected] = useState(false);

  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);

  const table = useReactTable({
    data: docs ?? fallbackTableData,
    columns,
    state: {
      rowSelection,
      columnFilters,
      globalFilter: showSelected,
    },
    globalFilterFn: selectedRowsFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
    initialState: {
      pagination: {
        pageSize: 15,
      },
      sorting: [{ id: 'file.name', desc: false }],
    },
    onRowSelectionChange: onRowSelectionChange,
    onColumnFiltersChange: setColumnFilters,
  });

  return (
    <section className="flex grow flex-col overflow-hidden px-4">
      <div className="my-4 flex flex-col gap-2">
        <div className="flex w-full justify-end">
          <Toggle
            pressed={showSelected}
            onPressedChange={() => setShowSelected(!showSelected)}
            className="whitespace-nowrap"
          >
            <Icon className={showSelected ? 'lucide--check-circle-2' : 'lucide--circle'} />
            <span>
              Show {selectedCount} Selected {selectedCount === 1 ? 'Document' : 'Documents'}
            </span>
          </Toggle>
        </div>

        <div className="flex gap-2">
          <SearchInput placeholder="Filter documents using natural language" className="grow" />

          <Popover>
            <PopoverTrigger asChild>
              <Button disabled={!docs}>
                <Icon className="lucide--funnel-plus" />
                <span>Add Filters</span>
              </Button>
            </PopoverTrigger>
            {docs && (
              <FilterPopover
                docs={docs}
                typeColumn={table.getColumn('type')}
                extensionColumn={table.getColumn('file.extension')}
                issuesColumn={table.getColumn('issues')}
                setColumnFilters={setColumnFilters}
              />
            )}
          </Popover>

          <Button disabled={!docs} onClick={() => setColumnFilters([])}>
            <Icon className="lucide--funnel-x" />
            <span>Clear All Filters</span>
          </Button>
        </div>

        <FilterPills pills={buildClientSideFilterPills(columnFilters, setColumnFilters)} />
      </div>

      <div className="mt-2 grow overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    <div
                      className="flex cursor-pointer items-center gap-2 select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon column={header.column} />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} onClick={() => row.toggleSelected()} className="cursor-pointer">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <TableFooter className="mb-4" table={table} plural="documents" />
    </section>
  );
}
